import { z } from "zod";
import { normalizePhone } from "./clients";

/** Settings → Phone assistant. */
export const voiceSettingsSchema = z.object({
  enabled: z.boolean(),
  transferNumber: z
    .string()
    .trim()
    .max(40)
    .transform((v, ctx) => {
      if (!v) return null;
      const n = normalizePhone(v);
      if (!n) ctx.addIssue({ code: "custom", message: "Enter a full phone number" });
      return n;
    }),
  messageStaffIds: z.array(z.string().uuid()).max(20),
  notes: z.string().max(4000),
});
export type VoiceSettingsInput = z.input<typeof voiceSettingsSchema>;

export interface VoiceAssistantView {
  enabled: boolean;
  hasKey: boolean;
  transferNumber: string | null;
  messageStaffIds: string[];
  notes: string;
  /** Where the call flow's tools send requests. */
  toolsUrl: string;
  /** Active staff, to pick who hears about messages. */
  staff: { id: string; name: string; hasMobile: boolean }[];
}

export type VoiceOutcome = "answered" | "texted_link" | "message" | "transferred";
export const VOICE_OUTCOME_LABELS: Record<VoiceOutcome, string> = {
  answered: "Answered",
  texted_link: "Texted a link",
  message: "Took a message",
  transferred: "Transferred",
};

export interface VoiceCallView {
  id: string;
  from: string;
  client: { id: string; name: string } | null;
  startedAt: string;
  endedAt: string | null;
  outcome: VoiceOutcome;
  summary: string | null;
  linksSent: string[];
  message: { name: string | null; text: string; callbackNumber: string | null; urgent: boolean } | null;
  handledAt: string | null;
  handledBy: string | null;
}

/** Connect sends every value as text: "true"/"yes" are true, anything else false. */
const flag = z.preprocess((v) => (typeof v === "string" ? /^(true|yes|1)$/i.test(v.trim()) : v), z.boolean()).default(false);

/** What the assistant can text the caller. */
export const VOICE_LINKS = ["booking", "mobile_booking", "event", "membership", "forms"] as const;
export type VoiceLink = (typeof VOICE_LINKS)[number];

/** One tool call from the call flow (through the Lambda): the key, the call, and the tool's inputs. */
export const voiceToolRequestSchema = z.object({
  key: z.string().min(10).max(200),
  tool: z.enum(["start_call", "clinic_info", "text_link", "take_message", "finish_call", "transfer_target"]),
  contactId: z.string().min(1).max(200),
  callerNumber: z.string().max(40).default(""),
  args: z.record(z.string(), z.unknown()).default({}),
});
export type VoiceToolRequest = z.input<typeof voiceToolRequestSchema>;

export const voiceTextLinkArgs = z.object({
  link: z.enum(VOICE_LINKS),
  /** For an event or a membership: which one, in the caller's words. */
  name: z.string().max(200).optional(),
});
export const voiceMessageArgs = z.object({
  callerName: z.string().trim().max(120).default(""),
  message: z.string().trim().min(1).max(2000),
  callbackNumber: z.string().max(40).optional(),
  urgent: flag,
});
export const voiceFinishArgs = z.object({
  summary: z.string().trim().max(2000).default(""),
  transferred: flag,
});
